import useResizeObserver from '@react-hook/resize-observer';
import { HierarchyNode } from 'd3';
import { FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { Loader } from 'components';
import { SelectedLocationContext } from 'contexts/selected-location';
import fetchWithRetry from 'services/fetch-retry';
import { Filter, FilterOperator, FilterState, LogicalOperator } from 'utils';

import { ChartComponentProps } from '../chart-card';
import TreeMap, { TreeMapData } from './tree-map';

const StatusUnits: FC<ChartComponentProps> = ({ boxRef, focused }) => {
  const navigate = useNavigate();
  const { selectedLocation } = useContext(SelectedLocationContext);

  const [counts, setCounts] = useState<{ unitStatus: string; unitCount: number }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);

  const [dimensions, setDimensions] = useState({ height: 0, width: 0 });
  useLayoutEffect(() => {
    setDimensions(() => ({
      width: boxRef.current?.clientWidth ?? 0,
      height: boxRef.current?.clientHeight ?? 0
    }));
  }, [boxRef]);
  useResizeObserver(boxRef.current, ({ contentBoxSize }) => {
    setDimensions({
      width: contentBoxSize[0].inlineSize,
      height: contentBoxSize[0].blockSize
    });
  });

  const getCounts = useCallback(async () => {
    setLoading(true);
    const url = new URL(`/api/units/counts/status?location=${selectedLocation?.id}`, window.location.origin);
    setCounts(await fetchWithRetry<typeof counts>(url, { method: 'GET' }, []));
    setLoading(false);
  }, [selectedLocation?.id]);

  useEffect(() => {
    getCounts();
  }, [getCounts]);

  const data: TreeMapData = useMemo(() => {
    let totalCount = 0;
    const children = counts.reduce(
      (acc: TreeMapData[], { unitStatus, unitCount }) => {
        totalCount += unitCount;
        if (/available/i.test(unitStatus)) {
          acc[0].children!.push({ category: 'AVAILABLE', name: unitStatus, value: unitCount });
        } else {
          acc[1].children!.push({ category: 'UNAVAILABLE', name: unitStatus, value: unitCount });
        }
        return acc;
      },
      [
        { name: 'AVAILABLE', children: [], colorScheme: ['#0F8E49'] },
        {
          name: 'UNAVAILABLE',
          children: [],
          colorScheme: [
            '#1223B2',
            '#1428CC',
            '#172DE5',
            '#1A32FF',
            '#3046FF',
            '#475AFF',
            '#5E6FFF',
            '#7584FF',
            '#8C98FF',
            '#A3ADFF',
            '#BAC1FF',
            '#D1D6FF',
            '#E8EAFF'
          ]
        }
      ]
    );
    setTotal(totalCount);
    return { name: 'STATUSES', children };
  }, [counts, setTotal]);

  const handleClick = useCallback(
    (_: PointerEvent, { data }: HierarchyNode<TreeMapData>) => {
      const filters: Filter[] = [
        { field: 'chassisStatus', value: data.name, filterOperator: FilterOperator.EQUALS },
        { field: 'gensetStatus', value: data.name, filterOperator: FilterOperator.EQUALS },
        { field: 'unitStatus', value: data.name, filterOperator: FilterOperator.EQUALS }
      ];
      const filterState: FilterState = { logicalOperator: LogicalOperator.OR, filters };
      navigate('/units', { state: { filterState } });
    },
    [navigate]
  );

  return (
    <div className="h-100">
      <Loader loading={loading}>
        <TreeMap
          customSort={(a, b) => Number(b.data.name === 'AVAILABLE') - Number(a.data.name === 'AVAILABLE')}
          data={data}
          handleClick={handleClick}
          total={total}
          focused={focused}
          {...dimensions}
        />
      </Loader>
    </div>
  );
};

export default StatusUnits;
